import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { Maybe } from '@storefront/lib-global/codegen/types';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { TypographyStyles, media } from '@storefront/lib-global/stylings';
import {
  ProductDescriptionBody,
  ProductDescriptionBodyFeatures,
  ProductDescriptionBodyText,
  ProductDescriptionButton,
  ProductDescriptionButtonContainer,
  ProductDescriptionContainer,
  ProductDescriptionHeaderText,
} from './ProductDescription.styles';

export type ProductDescriptionProps = {
  description?: Maybe<string>;
  features?: Maybe<string[]>;
};

export function ProductDescription({ features, description }: ProductDescriptionProps) {
  const { t } = useTranslation(['lib-products-common']);
  const router = useRouter();
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(true);
  const [lineHeight, setLineHeight] = useState('');
  const textElRef = useRef<HTMLParagraphElement>(null);

  const handleResize = useCallback(() => {
    const textEl = textElRef.current;
    if (textEl) {
      const lineHeightValue = window.getComputedStyle(textEl).lineHeight;
      const textHeight = textEl.scrollHeight;
      const numberOfLines = textHeight / parseInt(lineHeightValue, 10);
      setLineHeight(lineHeightValue);
      setIsTruncated(isDesktop ? numberOfLines > 3 : numberOfLines > 4);
    }
  }, [isDesktop]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize, router.asPath]);

  const handleClick = () => {
    setIsExpanded((prevState) => !prevState);
  };

  return (
    <ProductDescriptionContainer>
      <ProductDescriptionHeaderText>{t('pdp.description')}</ProductDescriptionHeaderText>
      <ProductDescriptionBody
        className={isTruncated && !isExpanded ? 'truncate' : ''}
        $lineHeight={lineHeight}
        $isExpanded={isExpanded}
      >
        <ProductDescriptionBodyText ref={textElRef}>{description}</ProductDescriptionBodyText>
        <ProductDescriptionBodyFeatures fontSize={TypographyStyles.Body.Small.Regular} text={features?.[0] || ''} />
      </ProductDescriptionBody>
      {isTruncated && (
        <ProductDescriptionButtonContainer>
          <ProductDescriptionButton type="button" onClick={handleClick}>
            {t(isExpanded ? 'pdp.read.less' : 'pdp.read.more')}
          </ProductDescriptionButton>
        </ProductDescriptionButtonContainer>
      )}
    </ProductDescriptionContainer>
  );
}

export default ProductDescription;
